import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { X, Plus, Edit2, Trash2 } from "lucide-react";
import { 
  addPromosTransactionDetail,
  updatePromosTransactionDetail,
  deletePromosTransactionDetail
} from '../Store/actions/transactionDetails';

const AdminEditModal = ({ isOpen, onClose, onSave }) => {
  const dispatch = useDispatch();
  const transactionDetails = useSelector(state => state.transactionDetails.promosTransactionDetails);
  
  const [newDetail, setNewDetail] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  useEffect(() => {
    // Load from Redux store on component mount
    // The initial state is already set in the reducer
  }, []);

  const handleSave = async () => {
    if (!onSave) {
      onClose();
      return;
    }
    try {
      await onSave(transactionDetails);
      onClose();
    } catch (error) {
      // Keep modal open so admin can retry
      console.error('Failed to save promo catalog:', error);
    }
  };

  const handleAddDetail = () => {
    if (newDetail.trim() && !transactionDetails.includes(newDetail.trim())) {
      dispatch(addPromosTransactionDetail(newDetail.trim()));
      setNewDetail("");
    }
  };

  const handleEditDetail = (index) => {
    setEditingIndex(index);
    setEditingValue(transactionDetails[index]);
  };

  const handleSaveEdit = () => {
    if (editingValue.trim() && !transactionDetails.includes(editingValue.trim())) {
      dispatch(updatePromosTransactionDetail(editingIndex, editingValue.trim()));
      setEditingIndex(null);
      setEditingValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue("");
  };

  const handleDeleteDetail = (index) => {
    dispatch(deletePromosTransactionDetail(index));
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl h-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <Card className="border-0 shadow-none h-full flex flex-col">
          <CardHeader className="bg-gray-50 border-b flex-shrink-0 p-3 sm:p-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-base sm:text-lg font-semibold text-gray-800 pr-2">
                Edit Transaction Details
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-3 sm:p-4 flex-1 overflow-hidden flex flex-col">
            <div className="space-y-4 sm:space-y-6 flex-1 overflow-hidden flex flex-col">
              {/* Add New Detail Section */}
              <div className="space-y-2 sm:space-y-3 flex-shrink-0">
                <h3 className="text-sm sm:text-base font-medium text-gray-700">
                  Add New Transaction Detail
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={newDetail}
                    onChange={(e) => setNewDetail(e.target.value)}
                    placeholder="Enter new detail..."
                    className="flex-1 text-sm"
                    onKeyPress={(e) => handleKeyPress(e, handleAddDetail)}
                  />
                  <Button
                    onClick={handleAddDetail}
                    disabled={!newDetail.trim()}
                    className="bg-yellow-400 hover:bg-yellow-500 text-black text-sm px-3 py-2 w-full sm:w-auto"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Existing Details List */}
              <div className="space-y-2 sm:space-y-3 flex-1 overflow-hidden flex flex-col">
                <h3 className="text-sm sm:text-base font-medium text-gray-700">
                  Existing Details ({transactionDetails.length})
                </h3>
                
                <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                  <div className="space-y-1 p-2">
                    {transactionDetails.map((detail, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {editingIndex === index ? (
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1">
                            <Input
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              className="flex-1 text-sm"
                              onKeyPress={(e) => handleKeyPress(e, handleSaveEdit)}
                              autoFocus
                            />
                            <div className="flex gap-1">
                              <Button
                                onClick={handleSaveEdit}
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 flex-1 sm:flex-none"
                              >
                                Save
                              </Button>
                              <Button
                                onClick={handleCancelEdit}
                                size="sm"
                                variant="outline"
                                className="text-xs px-2 py-1 flex-1 sm:flex-none"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="text-gray-700 flex-1 text-sm sm:text-base pr-2 leading-tight">
                              {detail}
                            </span>
                            <div className="flex gap-1 flex-shrink-0">
                              <Button
                                onClick={() => handleEditDetail(index)}
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-blue-100"
                              >
                                <Edit2 className="h-3 w-3" />
                              </Button>
                              <Button
                                onClick={() => handleDeleteDetail(index)}
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-red-100 text-red-600"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-3 sm:px-4 py-3 sm:py-4 flex-shrink-0">
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="text-sm px-4 py-2 order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="bg-yellow-400 hover:bg-yellow-500 text-black text-sm px-4 py-2 order-1 sm:order-2"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminEditModal;